/**
 * Gruppeninfo über dem Verlauf (MEIN-TAG-v15.md 12.4).
 *
 * Symbol, Name, Beschreibung, Mitglieder. Ändern dürfen nur Admins, und die
 * Änderung gilt für alle – es gibt einen Team-Chat, keine Sicht je Person.
 *
 * Das eigene Bild wird **im Browser** quadratisch zugeschnitten und auf
 * 512 px gebracht, bevor es den Rechner verlässt. Am Server landet damit
 * nie ein 8-Megapixel-Foto, und der Zuschnitt ist dort, wo man ihn sieht.
 */

import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { api } from '../api';
import { useKora } from '../zustand';
import { Symbolbild, type Gruppe } from './ChatPanel';

const VORLAGEN: { id: string; name: string }[] = [
  { id: 'monogramm', name: 'Monogramm' },
  { id: 'logo', name: 'Eichenherz-Logo' },
  { id: 'haus', name: 'Haus' },
  { id: 'fenster', name: 'Fenster' },
  { id: 'werkzeug', name: 'Werkzeug' },
  { id: 'herz', name: 'Herz' },
  { id: 'team', name: 'Team' },
];

/** Quadratisch zuschneiden und auf höchstens 512 px bringen. */
async function zuschneiden(datei: File): Promise<string> {
  const bild = await new Promise<HTMLImageElement>((ok, fehler) => {
    const i = new Image();
    i.onload = () => ok(i);
    i.onerror = () => fehler(new Error('Das Bild ließ sich nicht lesen.'));
    i.src = URL.createObjectURL(datei);
  });
  const kante = Math.min(bild.width, bild.height);
  const ziel = Math.min(512, kante);
  const tafel = document.createElement('canvas');
  tafel.width = ziel;
  tafel.height = ziel;
  const stift = tafel.getContext('2d');
  if (!stift) throw new Error('Der Browser kann das Bild nicht zuschneiden.');
  stift.drawImage(bild, (bild.width - kante) / 2, (bild.height - kante) / 2, kante, kante, 0, 0, ziel, ziel);
  URL.revokeObjectURL(bild.src);
  return tafel.toDataURL('image/jpeg', 0.86);
}

export function Gruppeninfo({
  gruppe,
  mitglieder,
  darfAendern,
  onSchliessen,
  onDirekt,
  onGeaendert,
}: {
  gruppe: Gruppe;
  mitglieder: { name: string; initialen: string; online: boolean; eigen: boolean; lage: string }[];
  darfAendern: boolean;
  onSchliessen: () => void;
  onDirekt: (name: string) => void;
  onGeaendert: () => void;
}) {
  const { toastZeigen } = useKora();
  const [name, setName] = useState(gruppe.name);
  const [beschreibung, setBeschreibung] = useState(gruppe.beschreibung);
  const [wahlOffen, setWahlOffen] = useState(false);
  const dateifeld = useRef<HTMLInputElement>(null);

  const speichern = useMutation({
    mutationFn: (daten: Partial<Gruppe> & { mitglieder?: string[] }) =>
      api.aendere<Gruppe>('/chat/gruppe', { raum: gruppe.raum, ...daten }),
    onSuccess: () => {
      onGeaendert();
      setWahlOffen(false);
    },
    onError: (f) => toastZeigen(f instanceof Error ? f.message : 'Änderung nicht möglich.'),
  });

  async function bildWaehlen(datei: File | undefined) {
    if (!datei) return;
    try {
      const daten = await zuschneiden(datei);
      speichern.mutate({ symbol: 'bild', bildUrl: daten });
    } catch (f) {
      toastZeigen(f instanceof Error ? f.message : 'Das Bild ließ sich nicht verwenden.');
    }
  }

  return (
    <div className="ch-info">
      <div className="ch-info-kopf">
        <span className="ch-info-titel">Gruppeninfo</span>
        <button type="button" className="ch-rund" title="Schließen" onClick={onSchliessen}>
          ✕
        </button>
      </div>

      <div className="ch-info-koerper">
        <div className="ch-info-symbol">
          <span className="ch-info-bild">
            <Symbolbild symbol={gruppe.symbol} bildUrl={gruppe.bildUrl} groesse={128} />
            {darfAendern && (
              <button
                type="button"
                className="ch-stift"
                title="Gruppenbild ändern"
                aria-label="Gruppenbild ändern"
                onClick={() => setWahlOffen((w) => !w)}
              >
                <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round">
                  <path d="M11.5 2.2l2.3 2.3-7.4 7.4-3 .7.7-3 7.4-7.4z" />
                </svg>
              </button>
            )}
          </span>
        </div>

        {wahlOffen && darfAendern && (
          <div className="ch-vorlagen">
            {VORLAGEN.map((v) => (
              <button
                key={v.id}
                type="button"
                className={`ch-vorlage ${gruppe.symbol === v.id ? 'aktiv' : ''}`}
                title={v.name}
                onClick={() => speichern.mutate({ symbol: v.id })}
              >
                <Symbolbild symbol={v.id} bildUrl="" groesse={44} />
              </button>
            ))}
            <button type="button" className="ch-vorlage eigen" onClick={() => dateifeld.current?.click()}>
              Eigenes Bild
            </button>
            <input
              ref={dateifeld}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              hidden
              onChange={(e) => void bildWaehlen(e.target.files?.[0])}
            />
          </div>
        )}

        <label className="ch-info-feld">
          <span>Name</span>
          <input
            value={name}
            disabled={!darfAendern}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => name.trim() && name !== gruppe.name && speichern.mutate({ name: name.trim() })}
          />
        </label>

        <label className="ch-info-feld">
          <span>Beschreibung</span>
          <textarea
            value={beschreibung}
            rows={2}
            disabled={!darfAendern}
            placeholder={darfAendern ? 'Wofür ist dieser Chat da?' : ''}
            onChange={(e) => setBeschreibung(e.target.value)}
            onBlur={() =>
              beschreibung !== gruppe.beschreibung && speichern.mutate({ beschreibung: beschreibung.trim() })
            }
          />
        </label>

        {!darfAendern && (
          <span className="ch-info-note">Name, Beschreibung und Symbol ändert das Admin Center.</span>
        )}

        <div className="ch-info-mitglieder">
          <span className="ch-info-kicker">
            {gruppe.istTeam
              ? `${mitglieder.length} Mitglieder · alle`
              : `${gruppe.mitglieder.length} Mitglieder`}
          </span>
          {/*
            Im Team-Chat ist jeder dabei – dort gibt es nichts zu wählen.
            In einer eigenen Gruppe schaltet der Admin die Leute zu und ab.
          */}
          {!gruppe.istTeam && darfAendern && (
            <div className="ch-info-wahl">
              {mitglieder.map((m) => {
                const drin = gruppe.mitglieder.includes(m.name);
                return (
                  <button
                    key={m.name}
                    type="button"
                    className={`ch-neu-person ${drin ? 'aktiv' : ''}`}
                    onClick={() =>
                      speichern.mutate({
                        mitglieder: drin
                          ? gruppe.mitglieder.filter((x) => x !== m.name)
                          : [...gruppe.mitglieder, m.name],
                      })
                    }
                  >
                    {m.name}
                  </button>
                );
              })}
            </div>
          )}
          {mitglieder.filter((m) => gruppe.istTeam || gruppe.mitglieder.includes(m.name)).map((m) => (
            <button
              key={m.name}
              type="button"
              className="ch-mitglied"
              onClick={() => !m.eigen && onDirekt(m.name)}
              disabled={m.eigen}
            >
              <span className="ch-avatar" style={{ width: 34, height: 34, fontSize: 11 }}>
                {m.initialen}
                {m.online && <span className="ch-punkt" />}
              </span>
              <span className="ch-mitglied-text">
                <span className="ch-mitglied-name">
                  {m.name}
                  {m.eigen ? ' · du' : ''}
                </span>
                <span className="ch-mitglied-lage">{m.lage}</span>
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
